// Sign-in with Google and Apple inside the app, through the operating system
// (Credential Manager on Android, GoogleSignIn and AuthenticationServices on iOS).
// Google refuses sign-in in an app's own web view, and the App Store asks for Sign in
// with Apple next to Google (guideline 4.8). The id_token goes to the same backend
// as on the web: POST /api/auth/oauth/native.
import { SocialLogin } from '@capgo/capacitor-social-login';
import { isIOS } from './platform';

const GOOGLE_WEB_CLIENT_ID = process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID;
const GOOGLE_IOS_CLIENT_ID = process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID;
const APPLE_CLIENT_ID = 'day.busybee.app';

// A build without the client ids (Codemagic group busybee_web) cannot sign in with
// Google at all: say so instead of failing somewhere inside the plugin.
export const googleConfigured = () => !!GOOGLE_WEB_CLIENT_ID && (!isIOS() || !!GOOGLE_IOS_CLIENT_ID);

let ready = null;
const init = () => {
  ready = ready || SocialLogin.initialize({
    ...(googleConfigured() ? {
      google: {
        webClientId: GOOGLE_WEB_CLIENT_ID,          // the token's audience: the backend checks it
        iOSClientId: GOOGLE_IOS_CLIENT_ID,
        iOSServerClientId: GOOGLE_WEB_CLIENT_ID,
        mode: 'online',
      },
    } : {}),
    ...(isIOS() ? { apple: { clientId: APPLE_CLIENT_ID } } : {}),
  }).catch((err) => {
    ready = null;  // the next tap tries again
    throw err;
  });
  return ready;
};

// Apple only on iOS: Android has no Sign in with Apple of its own and the rule is iOS's.
export const nativeProviders = () => (isIOS() ? ['google', 'apple'] : ['google']);

// The person closed the sheet: not an error to show.
export const isCancelled = (err) => /cancel/i.test(String(err?.message || err?.code || err));

export async function nativeSignIn(provider) {
  await init();
  if (provider === 'apple') {
    const { result } = await SocialLogin.login({ provider: 'apple', options: { scopes: ['email', 'name'] } });
    const name = [result.profile?.givenName, result.profile?.familyName].filter(Boolean).join(' ');
    return { idToken: result.idToken, fullName: name || undefined, authorizationCode: result.authorizationCode };
  }
  if (!googleConfigured()) throw new Error('Google sign-in is not set up in this build of the app');
  // No scopes: email, profile and openid are the plugin's defaults, and on Android any
  // `scopes` option is refused unless MainActivity is the plugin's own subclass.
  const { result } = await SocialLogin.login({ provider: 'google', options: {} });
  if (!result?.idToken) throw new Error('Google returned no id_token');
  return { idToken: result.idToken, fullName: result.profile?.name || undefined };
}
